'use client';

import Image, { type ImageProps } from 'next/image';
import { memo, useEffect, useState } from 'react';
import { CDN_URLS } from '@/helpers/constants';
import { blurImagePlaceholder } from '@/theme/placeholders';
import { isPrerenderContext } from '@/utils/prerender';
import cloudfrontLoader from './loader';

const getFallbackCdnSrc = (current: string | unknown): string => {
  // StaticImport (local/bundled) images don't go through a CDN — nothing to retry
  if (typeof current !== 'string') return blurImagePlaceholder;

  // Already on the placeholder — avoid triggering another error cycle
  if (current === blurImagePlaceholder) return blurImagePlaceholder;

  // Identify which CDN in the priority list served the failed URL
  const failedIndex = CDN_URLS.findIndex((cdn) => current.startsWith(cdn));

  // URL doesn't belong to any known CDN (external or relative src) — show placeholder
  if (failedIndex === -1) return blurImagePlaceholder;

  const nextCdn = CDN_URLS[failedIndex + 1];

  // Swap in the next CDN from the fallback chain and retry
  if (nextCdn) {
    try {
      const url = new URL(current);
      const fallback = new URL(nextCdn);
      url.protocol = fallback.protocol;
      url.host = fallback.host;
      return url.toString();
    } catch {
      return blurImagePlaceholder;
    }
  }

  // All CDNs exhausted — fall back to the blur placeholder
  return blurImagePlaceholder;
};

export const ImageComponent = memo(({ src, ...props }: ImageProps) => {
  const [imgSrc, setImgSrc] = useState(src || blurImagePlaceholder);

  useEffect(() => {
    if (src) {
      setImgSrc(src);
    }
  }, [src]);

  const handleError = () => {
    // Skip in prerender context: replacing src would write incorrect URLs into the Prerender.io snapshot.
    if (!isPrerenderContext()) {
      setImgSrc(getFallbackCdnSrc);
    }
  };

  return (
    <Image
      src={imgSrc}
      {...props}
      loader={cloudfrontLoader}
      onError={handleError}
    />
  );
});
